import type { PlainObject } from '@/types'

type FlattenObjectKeys<T extends Record<string, unknown>, Key = keyof T> = Key extends string
  ? T[Key] extends Record<string, unknown>
    ? `${Key}.${FlattenObjectKeys<T[Key]>}`
    : Key
  : never

export type MessageId = FlattenObjectKeys<typeof messages>

export type MessageProps = {
  id?: MessageId
  values?: PlainObject
}

const messages = {
  button: {
    accept: 'Accept',
    continue: 'Continue',
    reject: 'Reject',
    resetPassword: 'Forgot Password?',
    signIn: 'Sign In',
    signUp: "Don't Have an Account? Sign Up Here.",
    startTrial: 'Start a free trial today',
    submit: 'Submit',
  },
  errors: {
    404: '404 - Page Not Found',
    500: '500 - Server-side error occurred',
    invalidScope: 'Invalid Scope',
    maintenance: 'Under Maintenance',
    sso: {
      invalidScope: 'Invalid SSO Scope',
      accountExists: 'Missing accountExists param',
    },
    missingToken: 'Missing token param',
    missingSecurityChallengeToken: 'Missing security_challenge_token param',
    missingAuthParam: 'Missing callbackUrl param',
    missingOauthTokenOrJwtParam: 'Missing either oauthToken or jwt param',
    somethingWentWrong: 'Something went wrong. Please try again.',
    tryAgainSoon: 'Something went wrong. Please try again in a few moments.',
  },
  ssoLogin: {
    alreadyExists: 'We found an account with this email address. Enter your Neat password to continue signing up.',
    connectAccount: 'Connect your Neat account with %(service)s.',
    error: {
      title: 'Oops, it appears your sign-in request has failed.',
      content: 'Please enter your username and password below to continue.',
      unexpected: 'There was an unexpected error signing you in.',
    },
    header: {
      hasAccount: 'Welcome Back!',
      noAccount: 'Oops!',
    },
    noAccountMessage: {
      link1: 'Please try again',
      link2: 'contact support',
      message: 'if you feel you have received this message in error.',
    },
    noAccount: 'We did not find an account with this email address.',
    returnSignIn: 'Return to sign in',
    services: {
      apple: 'Apple',
      google: 'Google',
      intuit: 'Intuit',
    },
  },
  labels: {
    answer: 'Answer',
    confirmEmailAddress: 'Confirm Email Address',
    confirmPassword: 'Confirm Password',
    confirmResetPassword: 'New Password Confirmation *',
    firstName: 'First Name',
    lastName: 'Last Name',
    newPassword: 'New Password',
    newPasswordConfirmation: 'New Password Confirmation',
    password: 'Password',
    phoneNumber: 'Phone Number',
    rememberMe: 'Remember Me',
    resetPassword: 'New Password *',
    show: 'Show',
    starPassword: 'Password *',
    userEmail: 'Email Address (Username)',
    username: 'Username (Email Address)',
  },
  login: {
    copyright: {
      yearAndName: `Copyright 2019-${new Date().getFullYear()} %(companyName)s.`,
      rightsReserved: 'All rights reserved.',
    },
    dividerText: 'or',
    header: 'Sign in to your account',
    privacyPolicy: 'Privacy Policy',
    signUpLinkText: "Don't have an account?",
    termsAndConditions: 'Terms of Service',
    ssoError: {
      title: 'Oops, it appears your sign-in request has failed.',
      content: 'Please enter your username and password below to continue.',
    },
    lockedError: {
      title:
        'Your account has been locked due to too many failed login attempts. Please reset your password to unlock your account.',
      content: 'For more information and help with login issues, see this',
      link: 'Help Center article',
    },
    recaptchaError: 'An error occurred. Please try reloading the page. If the error persists, please',
    contactSupport: 'contact support.',
    oauthSignUp: 'Need a Neat Account?',
    tryFree: 'TRY FREE',
    requestValidationError:
      'Oops! We appear to be experiencing a temporary issue on our end validating your sign-in request and our engineers have been alerted. Please try again later.',
  },
  placeholders: {
    confirmEmail: 'Confirm Your Email Address',
    confirmPassword: 'Confirm Your Password',
    confirmResetPassword: 'Confirm new password',
    default: 'Enter %(label)s',
    newPassword: 'Enter new password',
    password: 'Enter your password',
    password2: 'Enter a Password',
    phoneNumber: 'Enter Your Phone Number',
    securityAnswer: 'Enter your answer',
    userEmail: 'Enter Your Email Address',
    username: 'myemail@company.com',
  },
  recoverPassword: {
    header: 'Reset Your Password',
    thankYou: 'Thank you!',
    success1:
      'If that email exists in our system we will send it a link to reset your password.  The link will be valid for %(time)s.',
    success2:
      "The email may take several minutes to be delivered.  If you don't see it, please check your spam folder.",
    success3: "Didn't get the email? ",
    success4: "If you can't access your email, please contact ",
    resendPassword: 'Resend the password reset link',
    customerSupport: 'Customer\xa0Support',
    returnSignIn: 'Return to sign in',
    resent: 'Reset password link is resent.',
  },
  resetPassword: {
    header: 'Reset Your Password',
    help: '*Your password should be 9-50 characters in length, including at least 1 number and at least 1 special character (e.g. !, @, #, $, %%, etc.). It cannot contain any spaces.',
    success: 'Your password has been reset successfully.',
  },
  ssoContinue: {
    cta: 'Continue to Neat',
    header: 'Success!',
    message: 'Your credentials have been authenticated.',
  },
  accountsGrid: {
    needsReview: 'Needs Review',
    unfiled: 'Unfiled',
    header: 'Welcome,',
    subheader: 'Choose Your Account',
    tooltip:
      'This is your primary account. All of your items sent to Neat via Mobile, E-mail, LDA, and Neat Connect will be found in this account.',
  },
  scanSnap: {
    accountSelection: {
      subheader: 'Choose an account to connect to ScanSnap',
      confirm: 'Confirm Authorization',
    },
  },
  billingStatus: {
    accountIssueHeader: 'There is an issue with %(accountModifier)s Neat account.',
    billing: 'Subscription Center',
    contactSupportLink: 'contact support',
    continue: 'Continue',
    expiredHeader: 'Your Neat subscription has expired.',
    expiredOwnerBody:
      'Welcome back %(name)s, your previous subscription is no longer active. However, signing up for a new plan is just a few clicks away. Visit the billing portal to find a plan and payment option that works best for you and access your items today!',
    expiredTrialHeader: '%(trialModifier)s trial period has ended.',
    expiredTrialOwnerBody:
      'Welcome back %(name)s, we hope you enjoyed your free trial! To continue using Neat, access the billing portal to find a plan and payment option that works best for you.',
    expiredTrialUserBody:
      'Welcome back %(name)s, the free trial on this account has ended. If you need access to this account and the saved items, we recommend contacting the owner to have the account reinstated.  You can also sign up for your own account in just a few clicks. Visit our website to find a plan and payment option that works best for you!',
    expiredUserBody:
      'Welcome back %(name)s, the subscription on this account is no longer active. If you need access to this account and the saved items, we recommend contacting the owner to have the account reinstated. You can also sign up for your own account in just a few clicks. Visit our website to find a plan and payment option that works best for you!',
    holdOwnerBody:
      '%(name)s, it appears your subscription has been paused due to a billing issue. Please update your payment information using our Subscription Center.',
    holdUserBody:
      '%(name)s, it appears your subscription has been paused due to a billing issue. Please have the account owner update their payment information using our Subscription Center.',
    noPlanBody: '%(name)s, an active subscription is required in order to use Neat and access your items. Please ',
    noPlanHeader: 'You do not have an active Neat subscription.',
    poorStandingOwnerBody:
      '%(name)s, we are unable to process your payment for this account. To avoid service interruption, please update your payment information using our Subscription Center.',
    poorStandingUserBody:
      '%(name)s, we are unable to process the payment for this account. To avoid service interruption, please have the account owner update the payment information using our Subscription Center.',
    return: 'Return',
    viewPlans: 'View Plans',
    featureNotSupportedHeader: 'This feature is not supported.',
    featureNotSupportedOwnerBody:
      'Welcome back %(name)s. To use this feature, access the billing portal to find a plan and payment option that works best for you.',
    featureNotSupportedUserBody:
      'Welcome back %(name)s. If you wish to use this feature, we recommend contacting the account owner.  You can also sign up for your own account in just a few clicks. Visit our website to find a plan and payment option that works best for you!',
  },
  authenticated: {
    success: 'Authentication Successful',
    redirect1: 'If you are not automatically redirected back to the %(appType)s app,',
    redirectLink: 'click here',
    redirect2: 'to be redirected to the web app.',
    redirect2WebApp: 'to be manually redirected.',
  },
  invitations: {
    login: {
      greeting: 'Hi %(name)s, ',
      notYou: 'NOT YOU?',
      pendingShare: 'You have a pending %(type)s share. \nPlease sign in to accept.',
    },
  },
  createAccount: {
    header: 'Create Your Free Account',
    subheader: 'All Fields Are Required',
    passRules:
      '*Your password should be 9-50 characters in length, including at least 1 number and at least 1 special character (e.g. !, @, #, $, %%, etc.). It cannot contain any spaces.',
    emailTaken: 'This email address is already in use. Please choose a different email address.',
    accountCreated: 'Your account has been created. Please sign in to accept the invitation.',
  },
  ssoAccessConfirmation: {
    header: 'Hi,',
    description:
      '%(clientName)s is requesting access to your Neat account.  Click “Accept” to grant them access, or click “Reject” to decline.',
  },
  mfa: {
    noCode: "Didn't receive a code? ",
    requestCode: 'Request a new code.',
    requestCodeDisabled: 'Request a new code in %(time)s.',
    noEmailAccess: "Can't access your email? Contact support.",
    invalidCode: 'The code you entered is not valid.',
    validTime: 'The code will be valid for %(expiryTime)s.',
    email: {
      enterCode: 'Enter below the six-digit code we sent to',
      noAccess: "Can't access your email? Contact support.",
    },
  },
  loginTimeout: {
    timedOut: 'The current login process has timed out.',
  },
} as const

export default messages
